import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface PlayerSeasonCar {
    season_id: number;
    season_name?: string;
    car_id?: number | null;
    car_name?: string | null;
    /**
     * Stored size of the uploaded car image, or null when there is none. The
     * image itself is NOT in this payload — build its URL via `carImageUrl()`.
     */
    image_bytes?: number | null;
    /** Owning player. */
    player_id?: number;
}

export interface SaveSeasonCar {
    season_id: number;
    car_id?: number | null;
    /** New image file to upload. */
    image?: File;
    /** Remove the existing image (ignored when `image` is provided). */
    removeImage?: boolean;
    /** Admin only: target another driver by their account name. */
    user_name?: string;
}

/**
 * Direct URL of a season-car image (binary response, long-lived browser
 * cache), or null when the entry has none. `image_bytes` busts the cache when
 * the image changes — same pattern as `avatarImageUrl()`.
 */
export function carImageUrl(entry: PlayerSeasonCar): string | null {
    if (entry.player_id == null || entry.image_bytes == null) return null;
    return `${environment.apiBaseUrl}/api/playerSeasonCar.php?image=1&player_id=${entry.player_id}&season_id=${entry.season_id}&v=${entry.image_bytes}`;
}

@Injectable()
export class PlayerSeasonCarService {
    private apiUrl = `${environment.apiBaseUrl}/api/playerSeasonCar.php`;

    constructor(private http: HttpClient) {}

    /** List the season cars of a player (member: own player_id). */
    getForPlayer(player_id: number): Observable<PlayerSeasonCar[]> {
        return this.http.get<PlayerSeasonCar[]>(this.apiUrl, {
            params: new HttpParams().set('player_id', player_id),
        });
    }

    /** Admin: list the season cars of the driver linked to the given user. */
    getForUser(user_name: string): Observable<PlayerSeasonCar[]> {
        return this.http.get<PlayerSeasonCar[]>(this.apiUrl, {
            params: new HttpParams().set('user_name', user_name),
        });
    }

    /** Admin: list every player's car for one season (each row carries player_id). */
    getForSeason(season_id: number): Observable<PlayerSeasonCar[]> {
        return this.http.get<PlayerSeasonCar[]>(this.apiUrl, {
            params: new HttpParams().set('season_id', season_id),
        });
    }

    /** Create or update the car for one (player, season). */
    save(data: SaveSeasonCar): Observable<PlayerSeasonCar> {
        const form = new FormData();
        form.append('season_id', String(data.season_id));
        if (data.car_id != null) form.append('car_id', String(data.car_id));
        if (data.image) form.append('image', data.image);
        if (data.removeImage) form.append('remove_image', '1');
        if (data.user_name) form.append('user_name', data.user_name);
        return this.http.post<PlayerSeasonCar>(this.apiUrl, form);
    }

    /** Remove the car entry for one (player, season). */
    remove(season_id: number, user_name?: string): Observable<{ success: boolean }> {
        return this.http.delete<{ success: boolean }>(this.apiUrl, {
            body: { season_id, ...(user_name ? { user_name } : {}) },
        });
    }
}
